/**
 * Hook for WebSocket connect/disconnect/send actions.
 */

import type { Dispatch } from '@reduxjs/toolkit'
import { useCallback } from 'react'

import { WS_ACTION_SEND_MESSAGE } from '../interfaces/websocket-events.interface'
import { wsConnect, wsDisconnect, wsSend } from '../store/middleware/websocket.middleware'
import type {
  WebSocketConnectionStatus,
  WebSocketConnectParams,
} from './agents-websocket.interface'

interface UseWebsocketActionsParams {
  dispatch: Dispatch
  setConnectionStatus: (status: WebSocketConnectionStatus) => void
  setConnectionError: (error: string | null) => void
  startCheck: () => void
  clearCheck: () => void
}

interface UseWebsocketActionsReturn {
  connect: (params: WebSocketConnectParams) => void
  disconnect: () => void
  sendMessage: (content: string, conversationId: string | null, agencySlug: string | null) => void
}

export const useWebsocketActions = ({
  dispatch,
  setConnectionStatus,
  setConnectionError,
  startCheck,
  clearCheck,
}: UseWebsocketActionsParams): UseWebsocketActionsReturn => {
  const connect = useCallback(
    ({ url, token, tenantId, userId, plan }: WebSocketConnectParams) => {
      setConnectionStatus('connecting')
      setConnectionError(null)
      dispatch(wsConnect({ url, token, tenantId, userId, plan }))
      startCheck()
    },
    [dispatch, setConnectionStatus, setConnectionError, startCheck]
  )

  const disconnect = useCallback(() => {
    dispatch(wsDisconnect())
    clearCheck()
    setConnectionStatus('disconnected')
  }, [dispatch, clearCheck, setConnectionStatus])

  const sendMessage = useCallback(
    (content: string, convId: string | null, agencySlug: string | null) => {
      dispatch(
        wsSend({
          action: WS_ACTION_SEND_MESSAGE,
          content,
          conversation_id: convId,
          agency_slug: agencySlug,
        })
      )
    },
    [dispatch]
  )

  return { connect, disconnect, sendMessage }
}
